import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { DurationService } from 'src/app/services/duration.service';
import { AccessFilter, StatsServerService, UserLabel } from 'src/app/services/jsonrpc/stats-server.service';
import { Role, Profile } from 'src/app/services/jsonrpc/user-server.service';

@Component({
  selector: 'app-access-control-modal',
  templateUrl: './access-modal.component.html',
  styleUrls: ['./access-modal.component.scss']
})
export class AccessControlModalComponent implements OnInit {
  @Input() public update: boolean;
  @Input() public filter: AccessFilter = { labels: {}, meta: { roles: [], users: [] } };
  @Input() public users: Profile[] = [];
  @Input() public roles: Role[] = [];

  @Output() public onSubmit = new EventEmitter<AccessFilter>();

  public labels: UserLabel[];
  public usernames: string[];

  public name: string;
  public labelKey: string;
  public labelValue: string;
  public labelIndex: number;
  public role: string;
  public user: string;

  public get title(): string {
    return (this.update ? 'Update' : 'Create') + ' Access Control Rule';
  }

  constructor(public stats: StatsServerService, public duration: DurationService, public modal: NgbActiveModal) {}

  public ngOnInit(): Promise<any> {
    if (this.users?.length > 0) {
      // setup sorted usernames
      this.usernames = this.users.map(p => p.username).sort();
    }

    if (this.update && this.filter.name) {
      // update: setup initial name
      this.name = this.filter.name;
    }

    if (this.update && this.filter.meta?.roles?.length > 0) {
      // update: setup initial selected role
      this.role = this.filter.meta.roles[0]; // allow only a single role for now
    }

    if (this.update && this.filter.meta?.users?.length > 0) {
      // update: setup initial selected role
      const uuid = this.filter.meta.users[0];
      this.user = this.users.find(p => p.uuid === uuid).username; // allow only a single role for now
      if (this.user != null && this.role == null) {
        this.role = ''; // indicate to apply the role to a specific user instead
      }
    }

    return this.stats.adminLabelList().then(labels => {
      if (this.update && this.filter.labels) {
        // update: setup initial selected label
        const keys = Object.keys(this.filter.labels);
        if (keys.length > 0) {
          this.labelKey = keys[0]; // allow only a single label for now
          this.labelValue = this.filter.labels[keys[0]];
          // if for some reason the master list does not contain this label: add it
          const existing = labels.find(l => l.key == this.labelKey);
          if (!existing) {
            labels.push({ key: this.labelKey, values: [this.labelValue] }); // add label key-value pair to master list
          } else if (!existing.values.includes(this.labelValue)) {
            existing.values.push(this.labelValue); // add missing value to existing label
          }
        }
      }
      this.labels = labels;
    });
  }

  public labelValues(key: string): string[] {
    return this.labels.find(l => l.key === key)?.values ?? [];
  }

  public isValid(): boolean {
    return this.isValidName() && this.isValidLabel() && this.isValidRoleOrUser();
  }

  public isValidName(): boolean {
    return this.name?.match(/^\w[\w\-\.+=#@ ]*[\w\+#]$/) != null;
  }

  public isValidLabel(): boolean {
    return this.labelKey != null;
  }

  public isValidRoleOrUser(): boolean {
    return (this.role != null && this.role !== '') || (this.user != null && this.user !== '');
  }

  public submit() {
    this.filter.name = this.name;
    this.filter.labels = {};
    this.filter.labels[this.labelKey] = this.labelValue;
    if (this.role === '' && this.user != null) {
      const uuid = this.users.find(p => p.username === this.user).uuid;
      this.filter.meta = { users: [uuid] };
    } else {
      this.filter.meta = { roles: [this.role] };
    }

    this.onSubmit.emit(this.filter);
    this.modal.close();
  }
}
